// localStream.js
// ★ 本地流处理器：完整克隆 StreamProcessor.fetchAndProcessData，
//   仅删除两处 store.dispatch('updateMessageToConversation')，
//   供「学习详情」弹窗使用 —— 消息自始至终不进主窗口 store，
//   从源头杜绝交互文本（诊断/闯关/回复选项）在主窗口出现副本。
import { getLevel } from '@/utils/chat/chatUtil';
import { getToken, getUser } from '@/utils/auth';
import { getBaseURL } from '@/utils/request';

// ★ 转发导出，供 streamResume.js 使用（避免循环依赖可直接从 '@/utils/request' 导）
export { getBaseURL };

export const CONTENT_TYPE_JSON = "application/json;charset=utf-8";
export const SPLIT_SINAL = '}\n{';

// ★★★ 公共分片解析器：主接口与续传接口共用，保证 offset 口径唯一 ★★★
// 后端契约：每次 yield 一个完整 JSON + '\n'；json.dumps 已转义字符串内换行，
// 因此裸 '}\n{' 只出现在分片边界，切分安全。
// 返回 { parsed: [json对象...] } 或 { parsed: null }（不完整，等待下一块）。
export function parseChunkBuffer(buf) {
  let chunk = buf;
  if (chunk.includes(SPLIT_SINAL)) {
    chunk = chunk
      .replaceAll(SPLIT_SINAL, '},{')
      .replaceAll('}\n', '}')
      .split(',')
      .join(',');
  }
  try {
    const parsed = JSON.parse(`[${chunk}]`);
    return { parsed, rest: '' };
  } catch (e) {
    return { parsed: null, rest: buf };   // 半截分片，原样保留等下一块
  }
}

// ★ 本地错误处理：克隆 FetchErrorHandler 的状态写入逻辑，
//   去掉 store.dispatch 与全局路由跳转/踢登录（弹窗流不干预全局会话）
function localSetErrorMessage(status, aiContent) {
  if (!['', 'loading'].includes(aiContent.msg)) return;
  aiContent.msg = "";
  aiContent.isDone = status;
  const messages = {
    2: '请求已取消',   // ABORT
    3: '网络出错',     // NETWORK
    401: '登录过期',   // EXPIRE（本地流仅标记，不踢登录）
    500: '服务器异常', // INTERAL
  };
  aiContent.msg = messages[status] || '服务器异常';
  if (aiContent.parts.length === 0) {
    aiContent.parts.push({ msg: aiContent.msg, questions: {} });
  }
}

export async function fetchLocalStream(url, data, handleDataFunction) {
  let readTimeout = null;
  let responseChunk = '';
  let { question, aiContent, params } = data;
  let options = {
    method: 'POST',
    signal: params.signal,
  };
  options.headers = new Headers({
    "Content-Type": CONTENT_TYPE_JSON,
    "Authorization": getToken(),
    "username": getUser() && getUser().phoneNumber,
  });

  let level = "";
  const withOutLevelUrls = ["/Composition"];
  if (question && question.level) { level = question.level; }
  if (!level) { level = getLevel(); }
  if (!withOutLevelUrls.some(target => url.includes(target))) {
    const { signal, ...bodyParams } = params;
    options.body = JSON.stringify({ ...bodyParams, level });
  }

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      console.log(response.status);
      localSetErrorMessage(response.status || 3, aiContent);
      return;   // ★ 改动 1：错误态直接返回，防止对错误响应继续读流
    }
    const reader = response.body.getReader();

    async function readChunk() {
      const { done, value } = await reader.read();
      if (done) {
        console.log('全部数据接收完毕');
        if (aiContent) {
          aiContent.isDone = 1;
          if (aiContent.msg === "loading") { aiContent.msg = ""; }
        }
        clearTimeout(readTimeout);
        if (question) { question.isLoading = false; }
        reader.releaseLock();
        return;
      }
      clearTimeout(readTimeout);
      responseChunk += new TextDecoder().decode(value);

      // ★ 改动 2：公共解析器解析 + offset 计数
      const { parsed, rest } = parseChunkBuffer(responseChunk);
      if (!parsed) return readChunk();          // 半截分片，等下一块再拼
      responseChunk = rest;

      for (const json of parsed) {
          // ★ 心跳帧：连接保活信号，不喂处理器、不计入 offset
        if (json && json.hb) {
            // 刷新流载体的心跳时间（isFlowStale 读 aiContent._lastHb）
            // question 也记一份，供旧版消费方兼容
            if (aiContent) aiContent._lastHb = Date.now();
            if (question) question._lastHb = Date.now();
            continue;
        }       
        handleDataFunction(json, aiContent, question);
        // ★ offset 计数：每成功处理 1 个完整 JSON 分片 +1
        //   与后端 entry.tokens.append 严格一一对应
        aiContent._receivedCount = (aiContent._receivedCount || 0) + 1;
      }
      return readChunk();
    }

    readChunk();
  } catch (error) {
    if (aiContent?._aborted) {
      clearTimeout(readTimeout);
      return;
    }
    question && (question.isLoading = false);
    if (!aiContent) return;
    aiContent.isSlow = false;
    if (error.name === 'AbortError') {
      localSetErrorMessage(2, aiContent);
      console.log('Request aborted:', error);
    } else {
      localSetErrorMessage(3, aiContent);
      console.error('Request failed:', error);
    }
  } finally {
    clearTimeout(readTimeout);
  }
}